import { GenericStatus } from '@policyfly/protobuf'
import {
  Parser,
  SchemaSubType,
  FormComponentApi_InputField_Type,
  FormComponentApi_Handler,
  FormComponentTextarea_InputType,
  FormComponentArrayGroup_AddHandler,
  FormComponentRateModifiers_DisplayMode,
  FormComponentTemplateUploader_Handler,
  FormComponentTable_TableHeader_Preset,
} from '@policyfly/protobuf/pfschema'

import type { ApiHandler, InputFieldType } from '../types/shared/formComponent/api'
import type { FormComponentArrayGroupAddHandler } from '../types/shared/formComponent/arrayGroup'
import type { DisplayMode } from '../types/shared/formComponent/rateModifiers'
import type { TableHeaderPreset } from '../types/shared/formComponent/table'
import type { TemplateUploaderHandler } from '../types/shared/formComponent/templateUploader'
import type { FormComponentTextareaInputType } from '../types/shared/formComponent/textarea'
import type { FormType } from '../types/shared/formTypes'
import type { ApplicationStatus } from '@policyfly/types/application'
import type { Parser as ParserSchema } from '@policyfly/utils/number'

type EnumLike<T> = {
  [id: string]: T | string
  [nu: number]: string
}
interface EnumMapper<T, U> {
  /**
   * Maps a configValue to a Protobuf enum.
   */
  mapValue(configValue: U): T
  /**
   * Reverts a protobuf enum back to a program configuration value.
   */
  revertEnum(protobuf: T): U
}
class EnumMappers extends WeakMap<EnumLike<unknown>, EnumMapper<unknown, unknown>> {
  addMapper<T, U> (e: EnumLike<T>, mapper: EnumMapper<T, U>): this {
    return this.set(e, mapper)
  }

  getMapper<T, U>(e: EnumLike<T>): EnumMapper<T, U> | undefined {
    return this.get(e) as EnumMapper<T, U> | undefined
  }
}
/**
 * Mappers transform a program configuration value into a protobuf message.
 */
export const enumMappers = new EnumMappers()

// shared
enumMappers.addMapper<GenericStatus, ApplicationStatus>(GenericStatus, {
  mapValue (configValue) {
    // TODO: Use protobuf mappers instead
    switch (configValue) {
      case 'DECLINED': return GenericStatus.CANCELLED_WITH_ERROR
      case 'DRAFT': return GenericStatus.DRAFT
      case 'ISSUED': return GenericStatus.IN_FORCE
      case 'PENDING_APPROVAL': return GenericStatus.PENDING_APPROVAL
      case 'PENDING_AUTHORISATION': return GenericStatus.AUTHORIZATION
      case 'PENDING_BINDER': return GenericStatus.PENDING_BINDER
      case 'PENDING_FINALISATION': return GenericStatus.PENDING_FINALISATION
      case 'PENDING_ISSUE': return GenericStatus.PENDING_ISSUE
      case 'PENDING_QUALITY_CONTROL': return GenericStatus.QUALITY_CONTROL
      case 'QUOTED': return GenericStatus.QUOTED
      case 'REQUESTED_TO_BIND': return GenericStatus.REQUESTED
      case 'REVIEW': return GenericStatus.SUBMITTED
      case 'TERMINATED': return GenericStatus.TERMINATED
      default: return GenericStatus.DRAFT
    }
  },
  revertEnum (protobuf) {
    switch (protobuf) {
      case GenericStatus.CANCELLED_WITH_ERROR: return 'DECLINED'
      case GenericStatus.DRAFT: return 'DRAFT'
      case GenericStatus.IN_FORCE: return 'ISSUED'
      case GenericStatus.PENDING_APPROVAL: return 'PENDING_APPROVAL'
      case GenericStatus.AUTHORIZATION: return 'PENDING_AUTHORISATION'
      case GenericStatus.PENDING_BINDER: return 'PENDING_BINDER'
      case GenericStatus.PENDING_FINALISATION: return 'PENDING_FINALISATION'
      case GenericStatus.PENDING_ISSUE: return 'PENDING_ISSUE'
      case GenericStatus.QUALITY_CONTROL: return 'PENDING_QUALITY_CONTROL'
      case GenericStatus.QUOTED: return 'QUOTED'
      case GenericStatus.REQUESTED: return 'REQUESTED_TO_BIND'
      case GenericStatus.SUBMITTED: return 'REVIEW'
      case GenericStatus.TERMINATED: return 'TERMINATED'
    }
    return 'DRAFT'
  },
})
enumMappers.addMapper<Parser, ParserSchema | undefined>(Parser, {
  mapValue (configValue) {
    switch (configValue) {
      case 'currency': return Parser.PARSER_CURRENCY
      case 'fixed1': return Parser.PARSER_FIXED_1
      case 'negative': return Parser.PARSER_NEGATIVE
      case 'percentage': return Parser.PARSER_PERCENTAGE
      default: return Parser.PARSER_UNSPECIFIED
    }
  },
  revertEnum (protobuf) {
    switch (protobuf) {
      case Parser.PARSER_CURRENCY: return 'currency'
      case Parser.PARSER_FIXED_1: return 'fixed1'
      case Parser.PARSER_NEGATIVE: return 'negative'
      case Parser.PARSER_PERCENTAGE: return 'percentage'
      default: return undefined
    }
  },
})
// form component
enumMappers.addMapper<SchemaSubType, FormType>(SchemaSubType, {
  mapValue (configValue) {
    switch (configValue) {
      case 'APP_INITIAL': return SchemaSubType.SCHEMA_SUB_TYPE_INITIAL
      case 'APP_PENDING_INFO': return SchemaSubType.SCHEMA_SUB_TYPE_PENDING_INFO
      case 'APP_ENDORSEMENT': return SchemaSubType.SCHEMA_SUB_TYPE_ENDORSEMENT
      case 'APP_RENEWAL': return SchemaSubType.SCHEMA_SUB_TYPE_RENEWAL
      case 'APP_RENEWAL_PENDING_INFO': return SchemaSubType.SCHEMA_SUB_TYPE_PENDING_INFO
      case 'QUOTE': return SchemaSubType.SCHEMA_SUB_TYPE_INITIAL
      case 'PREMIUM': return SchemaSubType.SCHEMA_SUB_TYPE_PENDING_INFO
      case 'ENDORSEMENT': return SchemaSubType.SCHEMA_SUB_TYPE_ENDORSEMENT
      case 'CANCELLATION': return SchemaSubType.SCHEMA_SUB_TYPE_CANCELLATION
      case 'BINDER': return SchemaSubType.SCHEMA_SUB_TYPE_INITIAL
      case 'ISSUE_INITIAL': return SchemaSubType.SCHEMA_SUB_TYPE_INITIAL
      case 'ISSUE_ENDORSEMENT': return SchemaSubType.SCHEMA_SUB_TYPE_ENDORSEMENT
      default: return SchemaSubType.SCHEMA_SUB_TYPE_INITIAL
    }
  },
  // TODO: This is lossy, we need to update how form types work
  revertEnum (protobuf) {
    switch (protobuf) {
      case SchemaSubType.SCHEMA_SUB_TYPE_INITIAL: return 'APP_INITIAL'
      case SchemaSubType.SCHEMA_SUB_TYPE_PENDING_INFO: return 'APP_PENDING_INFO'
      case SchemaSubType.SCHEMA_SUB_TYPE_ENDORSEMENT: return 'APP_ENDORSEMENT'
      case SchemaSubType.SCHEMA_SUB_TYPE_RENEWAL: return 'APP_RENEWAL'
      case SchemaSubType.SCHEMA_SUB_TYPE_CANCELLATION: return 'CANCELLATION'
      default: return 'APP_INITIAL'
    }
  },
})
enumMappers.addMapper<FormComponentApi_InputField_Type, InputFieldType | undefined>(FormComponentApi_InputField_Type, {
  mapValue (configValue) {
    switch (configValue) {
      case 'alphanumeric': return FormComponentApi_InputField_Type.TYPE_ALPHANUMERIC
      case 'number': return FormComponentApi_InputField_Type.TYPE_NUMBER
      case 'text': return FormComponentApi_InputField_Type.TYPE_TEXT
      default: return FormComponentApi_InputField_Type.TYPE_UNSPECIFIED
    }
  },
  revertEnum (protobuf) {
    switch (protobuf) {
      case FormComponentApi_InputField_Type.TYPE_ALPHANUMERIC: return 'alphanumeric'
      case FormComponentApi_InputField_Type.TYPE_NUMBER: return 'number'
      case FormComponentApi_InputField_Type.TYPE_TEXT: return 'text'
      default: return undefined
    }
  },
})
enumMappers.addMapper<FormComponentApi_Handler, ApiHandler | undefined>(FormComponentApi_Handler, {
  mapValue (configValue) {
    switch (configValue) {
      case 'bcapdBulkVinLookup': return FormComponentApi_Handler.HANDLER_BCAPD_BULK_VIN_LOOKUP
      default: return FormComponentApi_Handler.HANDLER_UNSPECIFIED
    }
  },
  revertEnum (protobuf) {
    switch (protobuf) {
      case FormComponentApi_Handler.HANDLER_BCAPD_BULK_VIN_LOOKUP: return 'bcapdBulkVinLookup'
      default: return undefined
    }
  },
})
enumMappers.addMapper<FormComponentTextarea_InputType, FormComponentTextareaInputType | undefined>(FormComponentTextarea_InputType, {
  mapValue (configValue) {
    switch (configValue) {
      case 'alphanumeric': return FormComponentTextarea_InputType.INPUT_TYPE_ALPHANUMERIC
      case 'date': return FormComponentTextarea_InputType.INPUT_TYPE_DATE
      case 'number': return FormComponentTextarea_InputType.INPUT_TYPE_NUMBER
      case 'text': return FormComponentTextarea_InputType.INPUT_TYPE_TEXT
      default: return FormComponentTextarea_InputType.INPUT_TYPE_UNSPECIFIED
    }
  },
  revertEnum (protobuf) {
    switch (protobuf) {
      case FormComponentTextarea_InputType.INPUT_TYPE_ALPHANUMERIC: return 'alphanumeric'
      case FormComponentTextarea_InputType.INPUT_TYPE_DATE: return 'date'
      case FormComponentTextarea_InputType.INPUT_TYPE_NUMBER: return 'number'
      case FormComponentTextarea_InputType.INPUT_TYPE_TEXT: return 'text'
      default: return undefined
    }
  },
})
enumMappers.addMapper<FormComponentArrayGroup_AddHandler, FormComponentArrayGroupAddHandler | undefined>(FormComponentArrayGroup_AddHandler, {
  mapValue (configValue) {
    switch (configValue) {
      case 'bcapdVinLookup': return FormComponentArrayGroup_AddHandler.ADD_HANDLER_BCAPD_VIN_LOOKUP
      default: return FormComponentArrayGroup_AddHandler.ADD_HANDLER_UNSPECIFIED
    }
  },
  revertEnum (protobuf) {
    switch (protobuf) {
      case FormComponentArrayGroup_AddHandler.ADD_HANDLER_BCAPD_VIN_LOOKUP: return 'bcapdVinLookup'
      default: return undefined
    }
  },
})
enumMappers.addMapper<FormComponentRateModifiers_DisplayMode, DisplayMode | undefined>(FormComponentRateModifiers_DisplayMode, {
  mapValue (configValue) {
    switch (configValue) {
      case 'rating': return FormComponentRateModifiers_DisplayMode.DISPLAY_MODE_RATING
      case 'summary': return FormComponentRateModifiers_DisplayMode.DISPLAY_MODE_SUMMARY
      default: return FormComponentRateModifiers_DisplayMode.DISPLAY_MODE_UNSPECIFIED
    }
  },
  revertEnum (protobuf) {
    switch (protobuf) {
      case FormComponentRateModifiers_DisplayMode.DISPLAY_MODE_RATING: return 'rating'
      case FormComponentRateModifiers_DisplayMode.DISPLAY_MODE_SUMMARY: return 'summary'
      default: return undefined
    }
  },
})
enumMappers.addMapper<FormComponentTemplateUploader_Handler, TemplateUploaderHandler | undefined>(FormComponentTemplateUploader_Handler, {
  mapValue (configValue) {
    switch (configValue) {
      case 'locations': return FormComponentTemplateUploader_Handler.HANDLER_LOCATIONS
      default: return FormComponentTemplateUploader_Handler.HANDLER_UNSPECIFIED
    }
  },
  revertEnum (protobuf) {
    switch (protobuf) {
      case FormComponentTemplateUploader_Handler.HANDLER_LOCATIONS: return 'locations'
      default: return undefined
    }
  },
})
enumMappers.addMapper<FormComponentTable_TableHeader_Preset, TableHeaderPreset | undefined>(FormComponentTable_TableHeader_Preset, {
  mapValue (configValue) {
    switch (configValue) {
      case 'yearsAgo': return FormComponentTable_TableHeader_Preset.PRESET_YEARS_AGO
      default: return FormComponentTable_TableHeader_Preset.PRESET_UNSPECIFIED
    }
  },
  revertEnum (protobuf) {
    switch (protobuf) {
      case FormComponentTable_TableHeader_Preset.PRESET_YEARS_AGO: return 'yearsAgo'
      default: return undefined
    }
  },
})
